/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EdgeProps, useReactFlow, BaseEdge } from 'reactflow';
import { X } from 'lucide-react';
import { toast } from "@/shared/components/ui/use-toast";
import { WorkflowBaseService } from '../services/WorkflowBaseService';

// Função auxiliar para calcular o caminho da curva
function createSmoothPath(sourceX: number, sourceY: number, targetX: number, targetY: number, calculateMidPoint = false): string | { path: string, midPoint: { x: number, y: number } } {
  // Detectamos se o edge é um loop (começa e termina próximo) ou se cruza horizontalmente
  const isLoop = Math.abs(sourceX - targetX) < 50 && Math.abs(sourceY - targetY) < 50;
  const isCrossingHorizontally = (sourceX > targetX); // Significa que o edge está indo da direita para a esquerda
  
  let path = '';
  let midPoint = { x: 0, y: 0 };
  
  if (isLoop) {
    // Para loops, criamos uma curva mais pronunciada para circular
    const radius = 50;
    const centerX = (sourceX + targetX) / 2 + radius;
    
    // Criamos um path que faz uma volta ampla
    path = `M${sourceX},${sourceY} 
            C${sourceX + radius * 1.5},${sourceY} 
            ${centerX + radius},${(sourceY + targetY) / 2} 
            ${centerX},${targetY + radius / 2}
            C${centerX},${targetY - radius / 2}
            ${targetX + radius * 1.5},${targetY}
            ${targetX},${targetY}`;
            
    // Calculamos o ponto médio do edge para loops
    midPoint = {
      x: centerX,
      y: (sourceY + targetY) / 2
    };
  } 
  else if (isCrossingHorizontally) {
    // Se estiver cruzando horizontalmente, fazemos uma curva ampla para evitar sobreposições
    const dx = Math.abs(targetX - sourceX);
    const dy = Math.abs(targetY - sourceY);
    
    // Calculamos uma curva que contorna pelo lado superior ou inferior dependendo da posição relativa
    const direction = sourceY > targetY ? -1 : 1;
    const amplitude = Math.max(100, dy * 1.5); // Amplitude mínima de 100px ou 1.5x a diferença vertical
    
    const controlPoint1X = sourceX + dx * 0.1;
    const controlPoint1Y = sourceY + direction * amplitude;
    const controlPoint2X = targetX - dx * 0.1;
    const controlPoint2Y = targetY + direction * amplitude;
    
    path = `M${sourceX},${sourceY} C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${targetX},${targetY}`;
    
    // Calculamos o ponto médio para curvas horizontais
    midPoint = {
      x: (sourceX + targetX) / 2,
      y: sourceY + direction * amplitude * 0.8
    };
  } 
  else {
    // Para edges normais, mantemos o comportamento padrão mas com mais curva
    const deltaX = Math.abs(targetX - sourceX) * 0.5;
    // Aumentamos a curvatura para evitar colisões
    const offsetY = Math.min(Math.abs(targetY - sourceY) * 0.3, 50) * (targetY > sourceY ? 1 : -1);
    
    const controlPoint1X = sourceX + deltaX * 0.3;
    const controlPoint1Y = sourceY + offsetY;
    const controlPoint2X = targetX - deltaX * 0.3;
    const controlPoint2Y = targetY - offsetY;
    
    path = `M${sourceX},${sourceY} C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${targetX},${targetY}`;
    
    // Calculamos o ponto médio para curvas normais
    midPoint = {
      x: (sourceX + targetX) / 2,
      y: (sourceY + targetY) / 2 - offsetY * 0.5
    };
  }
  
  if (calculateMidPoint) {
    return { path, midPoint };
  }
  
  return path;
}

// Interface estendida para incluir a propriedade data
interface CustomEdgeProps extends EdgeProps {
  data?: {
    input?: any;
    sourceHandle?: string;
    workflowId?: string;
    [key: string]: any;
  };
}

// Interface para o schema do workflow
interface WorkflowSchema {
  nodes: any[];
  edges: any[];
  [key: string]: any;
}

export default function CustomEdge({
  id,
  source,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  markerEnd,
  data,
}: CustomEdgeProps) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const { setEdges, getNodes } = useReactFlow();
  
  // Buscar o status do nó de origem
  const sourceNode = getNodes().find(node => node.id === source);
  const sourceNodeStatus = sourceNode?.data?.status;
  
  // Substituindo getBezierPath pelo nosso próprio algoritmo de caminho
  const pathResult = createSmoothPath(
    sourceX,
    sourceY,
    targetX,
    targetY,
    true
  ) as { path: string, midPoint: { x: number, y: number } };
  
  const edgePath = pathResult.path;
  const midPoint = pathResult.midPoint;

  // Corrigindo a propriedade hasInput com operador opcional
  const hasInput = data?.input != null;

  // Opções estilísticas para o caminho da aresta (linha)
  const edgeStyle = {
    ...style,
    stroke: hasInput ? style.stroke || '#a0aec0' : style.stroke || '#cbd5e1',  // Alterando para tons de cinza mais suaves
    strokeWidth: hasInput ? 3 : 2,
    strokeDasharray: '5,5',  // Sempre usar o estilo tracejado, independente de ter input ou não
    fill: 'none',
    pointerEvents: 'none' as const,
  };

  // Adiciona informação visual sobre o tipo de conexão (sourceHandle)
  const isLoopConnection = data?.sourceHandle === 'loop';
  const isExitConnection = data?.sourceHandle === 'exit';
  
  // Verifica se o nó de origem tem status "success", ou se a aresta foi marcada como processada com sucesso
  const isSuccess = sourceNodeStatus === 'success' || data?.sourceNodeStatus === 'success' || data?.success === true;

  // Aplica cores com base no status do nó de origem e tipo de conexão
  let edgeColor = edgeStyle.stroke;

  if (isSuccess) {
    edgeColor = '#22c55e'; 
  } else if (isLoopConnection) {
    edgeColor = '#3b82f6'; 
  } else if (isExitConnection) {
    edgeColor = '#f97316'; 
  }

  edgeStyle.stroke = edgeColor;

  const handleDelete = async (event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      // Remover o edge do estado local
      setEdges((edges) => edges.filter((edge) => edge.id !== id));

      // Verificar se o workflowId está definido
      if (!data?.workflowId) {
        console.error('WorkflowId não definido para este edge');
        return;
      }

      // Buscar o workflow atual
      const workflow = await WorkflowBaseService.getById(data.workflowId);

      // Garantir que settings é um objeto com a propriedade edges como um array
      const currentSchema: WorkflowSchema = { 
        nodes: [],
        edges: []
      };
      
      // Se temos settings e é um objeto, carregamos suas propriedades
      if (workflow?.settings && typeof workflow.settings === 'object') {
        // Copiamos as propriedades existentes
        Object.assign(currentSchema, workflow.settings);
        
        // Garantir que nodes e edges são arrays
        if (!Array.isArray(currentSchema.nodes)) {
          currentSchema.nodes = [];
        }
        if (!Array.isArray(currentSchema.edges)) {
          currentSchema.edges = [];
        }
      }

      // Atualizar o schema removendo o edge
      const updatedSchema: WorkflowSchema = {
        ...currentSchema,
        edges: currentSchema.edges.filter((e: any) => e.id !== id)
      };

      // Atualizar o banco
      await WorkflowBaseService.update(data.workflowId, { settings: updatedSchema });

      toast({
        title: t("workflow.edge.deleteSuccess"),
        description: t("workflow.edge.deleteSuccessDescription"),
      });

    } catch (error) {
      console.error('Error deleting edge:', error);
      toast({
        title: t("workflow.edge.deleteError"),
        description: t("workflow.edge.deleteErrorDescription"),
        variant: "destructive",
      });
    }
  };

  // Criar um path paralelo invisível para aumentar a área de hover
  const getHoverPath = () => {
    return createSmoothPath(sourceX, sourceY, targetX, targetY) as string;
  };

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group"
    >
      {/* Path invisível para aumentar área de hover */}
      <path
        d={getHoverPath()}
        style={{
          strokeWidth: 20,
          fill: 'none',
          stroke: 'transparent',
          cursor: 'pointer',
        }}
      />
      
      {/* Path visível do edge */}
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={edgeStyle} />
      
      {/* Botão de excluir */}
      {isHovered && (
        <g
          transform={`translate(${midPoint.x}, ${midPoint.y})`}
          className="cursor-pointer"
          onClick={handleDelete}
        >
          {/* Sombra do botão */}
          <circle
            r="14"
            className="fill-background/50"
            style={{
              filter: 'blur(4px)',
              transform: 'translate(2px, 2px)',
            }}
          />
          
          {/* Fundo do botão */}
          <circle
            r="14"
            className="fill-background stroke-border stroke-2 shadow-sm transition-all duration-200 hover:scale-110"
          />
          
          {/* Círculo de hover */}
          <circle
            r="12"
            className="fill-destructive/0 stroke-destructive/0 transition-all duration-200 hover:fill-destructive/10 hover:stroke-destructive"
          />
          
          {/* Container para centralizar o ícone X */}
          <foreignObject
            x="-8"
            y="-8"
            width="16"
            height="16"
            className="overflow-visible"
          >
            <div className="flex items-center justify-center w-full h-full">
              <X
                size={14}
                className="stroke-muted-foreground transition-colors duration-200 hover:stroke-destructive"
              />
            </div>
          </foreignObject>
        </g>
      )}
    </g>
  );
}
